import { useState, useEffect } from "react";
import fleetApi, { fleetError } from "@/lib/fleetApi";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Info, History } from "lucide-react";
import { fmtDateTime, fmtDayKey, dayKey } from "@/lib/time";

const darkField = "w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-600 [color-scheme:dark]";
const label = "mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400";

const pick = (c, ...keys) => keys.map((k) => c?.[k]).find((v) => v != null && v !== "");

export const ChangeRow = ({ c, i }) => {
  const who = pick(c, "actor", "changed_by_name", "changed_by", "user_name") || "Someone";
  const role = pick(c, "actor_role", "role");
  const oldP = pick(c, "old_pickup_local", "old_pickup", "old_value", "from");
  const newP = pick(c, "new_pickup_local", "new_pickup", "new_value", "to");
  const oldA = pick(c, "old_appointment_local", "old_appointment");
  const newA = pick(c, "new_appointment_local", "new_appointment");
  const when = pick(c, "ts", "changed_at", "created_at");
  return (
    <li className="rounded-lg border border-slate-800 bg-slate-900/60 px-3 py-2.5 text-xs" data-testid={`time-change-${i}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-semibold text-white">{who}{role ? <span className="ml-1 font-normal text-slate-500">({role})</span> : null}</span>
        <span className="text-slate-500">{when ? fmtDateTime(when) : ""}</span>
      </div>
      <div className="mt-1 text-slate-300">
        Pickup {oldP ?? "—"} → <span className="font-semibold text-blue-300">{newP ?? "—"}</span>
        {(oldA || newA) && <> · Appt {oldA ?? "—"} → <span className="font-semibold text-blue-300">{newA ?? "—"}</span></>}
      </div>
      {c?.reason && <div className="mt-1 text-slate-400">“{c.reason}”</div>}
    </li>
  );
};

export default function TripTimeDialog({ trip, open, onOpenChange, onUpdated }) {
  const [info, setInfo] = useState(null);
  const [pickup, setPickup] = useState("");
  const [appt, setAppt] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !trip) { return; }
    setInfo(null); setReason("");
    fleetApi.get(`/trips/${trip.id}/time-changes`).then((r) => {
      setInfo(r.data);
      setPickup(r.data.pickup_local || "");
      setAppt(r.data.appointment_local || "");
    }).catch((e) => { setInfo({ changes: [] }); toast.error(fleetError(e, "Couldn't load the trip's times.")); });
  }, [open, trip]);

  const unchanged = info && pickup === (info.pickup_local || "") && appt === (info.appointment_local || "");

  const submit = async () => {
    if (!/^\d{2}:\d{2}$/.test(pickup)) { toast.error("Enter a pickup time."); return; }
    if (reason.trim().length < 3) { toast.error("Please give a reason for the time change."); return; }
    setSaving(true);
    try {
      const body = { pickup_time: pickup, reason: reason.trim() };
      if (appt && appt !== (info?.appointment_local || "")) body.appointment_time = appt;
      await fleetApi.post(`/trips/${trip.id}/reschedule`, body);
      const { data } = await fleetApi.get(`/trips/${trip.id}`);
      toast.success(`Pickup moved to ${pickup} for ${trip.rider_name}. Only this trip was changed.`);
      onUpdated?.(trip.id, data);
      onOpenChange(false);
    } catch (e) {
      const code = e.response?.status;
      toast.error(code === 403 ? "You don't have permission to change trip times." : fleetError(e, "Time change failed."));
    } finally { setSaving(false); }
  };

  if (!trip) return null;
  const changes = info?.changes || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-slate-800 bg-[#0f172a] text-white" data-testid="trip-time-dialog">
        <DialogHeader>
          <DialogTitle>Change trip time</DialogTitle>
          <DialogDescription className="text-sm text-slate-400">
            {trip.external_trip_id} · {trip.rider_name} · {trip.scheduled_pickup ? fmtDayKey(dayKey(trip.scheduled_pickup)) : ""} · {trip.assigned_driver_name || "Unassigned"}
          </DialogDescription>
        </DialogHeader>
        {!info ? (
          <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-blue-400" /></div>
        ) : (
          <div className="space-y-4">
            <div className="flex gap-2 rounded-lg border border-blue-500/30 bg-blue-500/10 px-3 py-2.5 text-xs text-blue-200" data-testid="trip-time-scope-note">
              <Info className="mt-0.5 h-4 w-4 shrink-0" />
              This trip only. The standing order, the return leg and other drivers' trips stay the same, and the next import won't overwrite it.
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={label}>Pickup time</label>
                <input type="time" value={pickup} onChange={(e) => setPickup(e.target.value)} className={darkField} data-testid="trip-time-pickup" />
                <p className="mt-1 text-[11px] text-slate-500">Was {info.pickup_local || "—"}</p>
              </div>
              <div>
                <label className={label}>Appointment (optional)</label>
                <input type="time" value={appt} onChange={(e) => setAppt(e.target.value)} className={darkField} data-testid="trip-time-appointment" />
                <p className="mt-1 text-[11px] text-slate-500">Was {info.appointment_local || "—"}</p>
              </div>
            </div>
            <div>
              <label className={label}>Reason (required)</label>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
                placeholder="e.g. Rider asked for a later pickup" data-testid="trip-time-reason"
                className="border-slate-700 bg-slate-900 text-white placeholder:text-slate-500" />
            </div>
            <div>
              <div className={`${label} flex items-center gap-1.5`}><History className="h-3.5 w-3.5" /> Change history</div>
              {changes.length === 0
                ? <p className="text-xs text-slate-500" data-testid="trip-time-history-empty">No time changes yet.</p>
                : <ul className="space-y-2" data-testid="trip-time-history">{changes.map((c, i) => <ChangeRow key={c.id || i} c={c} i={i} />)}</ul>}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-300 hover:bg-slate-800 hover:text-white" data-testid="trip-time-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving || !info || unchanged || reason.trim().length < 3} className="gap-2 bg-blue-700 text-white hover:bg-blue-800" data-testid="trip-time-confirm">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save new time
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
